// Command-based undo (DRAW_PLAN §4): every mutation is a Command whose recipe edits an Immer draft. The
// history keeps Immer patches + inverse patches, so undo/redo are cheap and the document is never copied
// whole. Commands with the same `merge` key in a row (a drag) become one undo step. Capped at 500 steps.
import { applyPatches, enablePatches, produceWithPatches, type Patch } from 'immer';
import type { Doc } from '../model/types';

enablePatches();

export type Command = {
  /** Shown in the History panel (a message key or plain text). */
  label: string;
  recipe: (draft: Doc) => void;
  /** Consecutive commands with the same merge key are one undo step (one per gesture). */
  merge?: string;
};
export type HistoryEntry = { label: string; patches: Patch[]; inverse: Patch[]; merge?: string };
export type History = { past: HistoryEntry[]; future: HistoryEntry[] };

export const HISTORY_CAP = 500;
export const emptyHistory = (): History => ({ past: [], future: [] });

/** Applies `cmd`; returns the new document and history (both new objects; the old ones are untouched). */
export function execute(doc: Doc, h: History, cmd: Command): { doc: Doc; history: History } {
  const [next, patches, inverse] = produceWithPatches(doc, cmd.recipe);
  if (!patches.length) return { doc, history: h };
  const last = h.past[h.past.length - 1];
  let past: HistoryEntry[];
  if (cmd.merge && last && last.merge === cmd.merge)
    past = [
      ...h.past.slice(0, -1),
      {
        label: last.label,
        merge: cmd.merge,
        ...mergePatches(last, patches, inverse),
      },
    ];
  else past = [...h.past, { label: cmd.label, merge: cmd.merge, patches, inverse }];
  if (past.length > HISTORY_CAP) past = past.slice(past.length - HISTORY_CAP);
  return { doc: next, history: { past, future: [] } };
}

/**
 * One gesture's patches. A drag replaces the same values every frame: keep only the newest forward patch and
 * the oldest inverse patch per path (when every patch is a plain replace), so a long drag does not grow.
 */
function mergePatches(
  last: HistoryEntry,
  patches: Patch[],
  inverse: Patch[],
): { patches: Patch[]; inverse: Patch[] } {
  const simple = (ps: Patch[]) => ps.every((p) => p.op === 'replace');
  if (!simple(patches) || !simple(inverse) || !simple(last.patches) || !simple(last.inverse))
    return { patches: [...last.patches, ...patches], inverse: [...inverse, ...last.inverse] };
  const key = (p: Patch) => JSON.stringify(p.path);
  const newKeys = new Set(patches.map(key));
  const oldInv = new Set(last.inverse.map(key));
  return {
    patches: [...last.patches.filter((p) => !newKeys.has(key(p))), ...patches],
    inverse: [...inverse.filter((p) => !oldInv.has(key(p))), ...last.inverse],
  };
}

/** Ends the current gesture: the next command never merges into the last one. */
export function seal(h: History): History {
  const last = h.past[h.past.length - 1];
  if (!last?.merge) return h;
  return { ...h, past: [...h.past.slice(0, -1), { ...last, merge: undefined }] };
}

export function undo(doc: Doc, h: History): { doc: Doc; history: History } {
  const e = h.past[h.past.length - 1];
  if (!e) return { doc, history: h };
  return {
    doc: applyPatches(doc, e.inverse),
    history: { past: h.past.slice(0, -1), future: [{ ...e, merge: undefined }, ...h.future] },
  };
}

export function redo(doc: Doc, h: History): { doc: Doc; history: History } {
  const e = h.future[0];
  if (!e) return { doc, history: h };
  return { doc: applyPatches(doc, e.patches), history: { past: [...h.past, e], future: h.future.slice(1) } };
}
